import React, { useState } from 'react';
import { BufferGeometry, MeshBasicMaterial } from 'three';

import { RoofContainer } from '@paragon/contract-library';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { handleRoofFacePicked } from '../../store/actions/layout/layoutPickingActions';
import { handleStandardSceneItemRightClick } from '../../store/actions/sceneInteractionActions';
import { clearHoverItemIfActive, ensureHoverItemIsActive } from '../../store/actions/viewportActions';
import { getPickingObjectInfo } from '../../store/selectors/pickingSelectors';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { layoutColors } from './layoutColors';

const roofSolidMaterial = new MeshBasicMaterial({ color: layoutColors.roofMesh.primary });
const alternateRoofSolidMaterial = new MeshBasicMaterial({ color: layoutColors.roofMesh.alternate });

interface Props {
  readonly roofContainer: RoofContainer;
  readonly roofSolidBufferGeometry: BufferGeometry;

  readonly wireframe?: boolean;
}

export function RoofSolidVisual({ roofContainer, roofSolidBufferGeometry, wireframe }: Props) {
  const dispatch = useAppDispatch();

  const pickingObjectInfo = useAppSelector(getPickingObjectInfo);
  const isPickingRoofFace = pickingObjectInfo?.type === 'pickingRoofFace';

  const [hoveredMaterialIndex, setHoveredMaterialIndex] = useState<number | null>(null);

  const materials = [];
  for (let i = 0; i < roofSolidBufferGeometry.groups.length; i++) {
    materials.push(isPickingRoofFace && i === hoveredMaterialIndex ? alternateRoofSolidMaterial : roofSolidMaterial);
  }

  const hoverItem = { type: 'roofContainer', guid: roofContainer.guid, origin: 'fiber' } as const;

  const showFaceMaterial = isPickingRoofFace || !wireframe;

  return (
    <group
      onPointerMove={(event) => {
        if (isPickingRoofFace) {
          setHoveredMaterialIndex(event.face!.materialIndex);
        }
      }}
      onPointerOver={(event) => {
        event.stopPropagation();
        dispatch(ensureHoverItemIsActive(hoverItem));
      }}
      onPointerOut={() => {
        setHoveredMaterialIndex(null);
        dispatch(clearHoverItemIfActive(hoverItem));
      }}
      onClick={async (event) => {
        if (threeJsDom.shouldDispatchClickEvent()) {
          if (pickingObjectInfo?.type === 'pickingRoofFace') {
            event.stopPropagation();
            await dispatch(
              handleRoofFacePicked(pickingObjectInfo.targetInfo, roofContainer.solidData!, event.face!.materialIndex),
            );
            return;
          }
        }
      }}
      onContextMenu={async (event) => {
        await dispatch(
          handleStandardSceneItemRightClick(event, {
            type: 'roofContainer',
            guid: roofContainer.guid,
            origin: 'fiber',
          }),
        );
      }}
    >
      {showFaceMaterial && <mesh geometry={roofSolidBufferGeometry} material={materials} />}

      <lineSegments>
        <lineBasicMaterial color={wireframe ? layoutColors.roofLines : layoutColors.solidMeshEdges} linewidth={5} />
        <edgesGeometry args={[roofSolidBufferGeometry]} />
      </lineSegments>
    </group>
  );
}
